import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  Pagination,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Stack,
  Divider,
} from '@mui/material';
import {
  Search,
  Refresh,
  CheckCircle,
  Cancel,
  Flag,
  Block,
  PlayArrow,
  Person,
  Home,
  CalendarToday,
  Notes,
  Visibility,
} from '@mui/icons-material';
import { ModerationRecord, ModerationAuditFilters } from '@/types';
import { apiClient } from '@/utils/api-client';
import { useDebounce } from '@/hooks/useDebounce';

interface ModerationAuditLogProps {
  resourceId?: string; // If provided, shows audit log for specific resource
  resourceType?: 'listing' | 'user' | 'booking'; // Filter by resource type
  onError?: (error: string) => void;
}

export const ModerationAuditLog: React.FC<ModerationAuditLogProps> = ({ 
  resourceId, 
  resourceType,
  onError 
}) => {
  const [records, setRecords] = useState<ModerationRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecord, setSelectedRecord] = useState<ModerationRecord | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  
  // Filters and pagination
  const [filters, setFilters] = useState<ModerationAuditFilters>({
    resourceType: resourceType,
    resourceId: resourceId,
    search: '',
    sortBy: 'moderatedAt',
    sortOrder: 'desc',
    limit: 20,
    offset: 0,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  
  // Debounced search
  const debouncedSearch = useDebounce(filters.search || '', 500);

  const fetchAuditRecords = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      if (filters.resourceType) queryParams.append('resourceType', filters.resourceType);
      if (filters.resourceId) queryParams.append('resourceId', filters.resourceId);
      if (filters.moderatedBy) queryParams.append('moderatedBy', filters.moderatedBy);
      if (filters.action) queryParams.append('action', filters.action);
      if (filters.dateFrom) queryParams.append('dateFrom', filters.dateFrom);
      if (filters.dateTo) queryParams.append('dateTo', filters.dateTo);
      if (debouncedSearch) queryParams.append('search', debouncedSearch);
      if (filters.sortBy) queryParams.append('sortBy', filters.sortBy);
      if (filters.sortOrder) queryParams.append('sortOrder', filters.sortOrder);
      if (filters.limit) queryParams.append('limit', filters.limit.toString());
      if (filters.offset) queryParams.append('offset', filters.offset.toString());

      const response = await apiClient.get<{
        records: ModerationRecord[];
        total: number;
        hasMore: boolean;
      }>(`/admin/moderation/audit?${queryParams.toString()}`);

      if (response.success && response.data) {
        setRecords(response.data.records);
        setTotalCount(response.data.total);
      } else {
        throw new Error('Failed to fetch audit records');
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to load audit records';
      setError(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [filters, debouncedSearch, onError]);

  useEffect(() => {
    fetchAuditRecords();
  }, [fetchAuditRecords]);

  const handleFilterChange = (key: keyof ModerationAuditFilters, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
      offset: 0, // Reset to first page when filters change
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    setFilters(prev => ({
      ...prev,
      offset: (page - 1) * (prev.limit || 20),
    }));
  };

  const openDetailDialog = (record: ModerationRecord) => {
    setSelectedRecord(record);
    setDetailDialogOpen(true);
  };

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'approve': return <CheckCircle color="success" />;
      case 'reject': return <Cancel color="error" />;
      case 'flag': return <Flag color="warning" />;
      case 'suspend': return <Block color="error" />;
      case 'activate': return <PlayArrow color="success" />;
      default: return null;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case 'approve': return 'success';
      case 'reject': return 'error';
      case 'flag': return 'warning';
      case 'suspend': return 'error';
      case 'activate': return 'success';
      default: return 'default';
    }
  };

  const getResourceTypeIcon = (resourceType: string) => {
    switch (resourceType) {
      case 'listing': return <Home />;
      case 'user': return <Person />;
      case 'booking': return <CalendarToday />;
      default: return null;
    }
  };

  const totalPages = Math.ceil(totalCount / (filters.limit || 20));

  if (loading && records.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
          Moderation Audit Log
          {resourceId && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Showing history for {resourceType} ID: {resourceId}
            </Typography>
          )}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={fetchAuditRecords}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Filters */}
      {!resourceId && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Search records"
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Resource Type</InputLabel>
                  <Select
                    value={filters.resourceType || ''}
                    label="Resource Type"
                    onChange={(e) => handleFilterChange('resourceType', e.target.value || undefined)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="listing">Listings</MenuItem>
                    <MenuItem value="user">Users</MenuItem>
                    <MenuItem value="booking">Bookings</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    value={filters.action || ''}
                    label="Action"
                    onChange={(e) => handleFilterChange('action', e.target.value || undefined)}
                  >
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="approve">Approve</MenuItem>
                    <MenuItem value="reject">Reject</MenuItem>
                    <MenuItem value="flag">Flag</MenuItem>
                    <MenuItem value="suspend">Suspend</MenuItem>
                    <MenuItem value="activate">Activate</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="From Date"
                  type="date"
                  value={filters.dateFrom || ''}
                  onChange={(e) => handleFilterChange('dateFrom', e.target.value || undefined)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="To Date"
                  type="date"
                  value={filters.dateTo || ''}
                  onChange={(e) => handleFilterChange('dateTo', e.target.value || undefined)}
                  InputLabelProps={{ shrink: true }}
                  size="small"
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Audit Records Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Resource</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Moderator</TableCell>
                <TableCell>Reason</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.map((record) => (
                <TableRow key={record.moderationId} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ width: 32, height: 32, mr: 2, bgcolor: 'primary.light' }}>
                        {getResourceTypeIcon(record.resourceType)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                          {record.resourceType}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {record.resourceId.substring(0, 8)}...
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getActionIcon(record.action)}
                      <Chip
                        label={record.action}
                        color={getActionColor(record.action) as any}
                        size="small"
                        sx={{ ml: 1, textTransform: 'capitalize' }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{record.moderatedByName}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        maxWidth: 200, 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {record.reason}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(record.moderatedAt).toLocaleDateString()}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(record.moderatedAt).toLocaleTimeString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      startIcon={<Visibility />}
                      onClick={() => openDetailDialog(record)}
                    >
                      Details
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        {records.length === 0 && !loading && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body1" color="text.secondary">
              No moderation records found matching your criteria.
            </Typography>
          </Box>
        )}
      </Card>

      {/* Record Detail Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {selectedRecord && getActionIcon(selectedRecord.action)}
            <Typography variant="h6" sx={{ ml: 1 }}>
              Moderation Record Details
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRecord && (
            <Box>
              {/* Basic Info */}
              <Box sx={{ mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Resource Type
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      {getResourceTypeIcon(selectedRecord.resourceType)}
                      <Typography variant="body1" sx={{ ml: 1, textTransform: 'capitalize' }}>
                        {selectedRecord.resourceType}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Resource ID
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5, fontFamily: 'monospace' }}>
                      {selectedRecord.resourceId}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Action
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                      <Chip
                        label={selectedRecord.action}
                        color={getActionColor(selectedRecord.action) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {new Date(selectedRecord.moderatedAt).toLocaleString()}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Moderator Info */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Moderator Information
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.moderatedByName}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ID: {selectedRecord.moderatedBy}
                </Typography>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Status Change */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Status Change
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Chip 
                    label={selectedRecord.previousStatus} 
                    size="small" 
                    variant="outlined"
                    sx={{ textTransform: 'capitalize' }}
                  />
                  <Typography sx={{ mx: 2 }}>→</Typography>
                  <Chip 
                    label={selectedRecord.newStatus} 
                    size="small"
                    color={getActionColor(selectedRecord.action) as any}
                    sx={{ textTransform: 'capitalize' }}
                  />
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Reason */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Reason
                </Typography>
                <Typography variant="body1">
                  {selectedRecord.reason}
                </Typography>
              </Box>

              {/* Notes */}
              {selectedRecord.notes && (
                <Box>
                  <Divider sx={{ my: 2 }} />
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 'bold' }}>
                      Additional Notes
                    </Typography>
                    <Typography variant="body1">
                      {selectedRecord.notes}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};